import { useCallback, useMemo, useState } from "react";
import type {
  AvailableColumn,
  PipelineStatus,
  ProjectMember,
  SavedViewEntity,
  SavedViewRead,
} from "@/api/types";
import {
  buildFieldOptions,
  deserializeFilters,
  OPERATORS_BY_TYPE,
  serializeFilters,
  type FieldOption,
  type FilterCondition,
} from "@/components/companies/AdvancedFilters";
import {
  applyLabelOverrides,
  deserializeSorts,
  operatorLabel,
  pickerFields,
  TAB_CHIP_ID,
  TAB_ORDER,
  TAB_SPECS,
  valueLabelFor,
  type ActiveFilter,
  type SortSpec,
  type TabKey,
} from "@/components/companies/filterTypes";
import { useDebounce } from "@/hooks/useDebounce";
import { CFO_ACTION_LABELS } from "@/lib/cfoActions";
import { pickString } from "@/lib/viewFilters";

/** Stable local id for a fresh FilterCondition — used by the chip
 *  edit/add handlers when creating rows. */
export function makeFilterId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `cond-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** The saved-view bucket of a tab. The "all" tab piggybacks on
 *  companies_prospects: saved-view buckets are scoped to the three
 *  pre-existing tabs. */
export function companyViewEntity(tab: TabKey): SavedViewEntity {
  return tab === "clients"
    ? "companies_clients"
    : tab === "archived"
      ? "companies_archived"
      : "companies_prospects";
}

/**
 * The Companies page's filter state — tab, search, filter tree, sorts —
 * and what a companies list request takes from it. Shared by the
 * Companies page and every list that offers the same filters (the Email
 * wizard), so both send the backend the same parameters.
 */
export function useCompanyFilterState(initialTab: TabKey = "prospects") {
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [search, setSearch] = useState("");
  // Empty search resolves synchronously — no debounce tick — so
  // clearing the field (X button, Escape, backspace-to-empty, or the
  // global "Clear filters" button) settles in the same render and
  // triggers exactly one refetch. Non-empty input still debounces.
  const debouncedSearchRaw = useDebounce(search, 250);
  const debouncedSearch = search === "" ? "" : debouncedSearchRaw;
  // The unified filter set — every UI control (saved view apply,
  // chip add/edit/remove, "+ Add filter" picker) writes into this
  // single array. Each entry is one FilterCondition (field +
  // operator + value); status and score conditions live here too.
  const [advancedFilters, setAdvancedFilters] = useState<FilterCondition[]>([]);
  const advancedFiltersParam = useMemo(() => serializeFilters(advancedFilters), [advancedFilters]);
  // Multi-sort state. Empty list → backend's default ordering
  // (effective_score DESC, id DESC). Array order = sort priority.
  const [sorts, setSorts] = useState<SortSpec[]>([]);
  // Signature of the sort state, for effects that react to a sort change.
  const sortsParam = useMemo(() => JSON.stringify(sorts), [sorts]);

  // The tab adds an implicit `is_client` + `is_archived` scope. If
  // the operator overrides either via the advanced builder (e.g.
  // adds `is_archived = true` to surface archived rows), the implicit
  // is suppressed so the user's intent wins.
  const advancedConditionFields = useMemo(() => new Set(advancedFilters.map((f) => f.field)), [advancedFilters]);

  // Filter-only params shared by every consumer (listing, export,
  // bulk actions, the Email wizard) so they can't drift on a new
  // filter. Excludes pagination — each consumer adds page / page_size.
  const listFilters = useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    if (debouncedSearch.trim()) out.q = debouncedSearch.trim();
    if (advancedFiltersParam) out.filters = advancedFiltersParam;
    if (tab === "archived") {
      // Archived view: ONLY archived rows, whatever their client status.
      out.include_archived = true;
      out.archived_only = true;
    } else if (tab === "all") {
      // "All" tab: no implicit scope — non-archived companies across
      // both client states.
    } else {
      if (!advancedConditionFields.has("is_client")) {
        out.is_client = tab === "clients";
      }
      // Default scope is non-archived, unless the operator asked for
      // archived rows with an `is_archived` condition.
      if (!advancedConditionFields.has("is_archived")) {
        out.include_archived = false;
      }
    }
    // Multi-sort → comma-separated sort_by + order params.
    if (sorts.length > 0) {
      out.sort_by = sorts.map((s) => s.field).join(",");
      out.order = sorts.map((s) => s.dir).join(",");
    }
    return out;
  }, [tab, debouncedSearch, advancedFiltersParam, advancedConditionFields, sorts]);

  const entity = companyViewEntity(tab);
  const anyFilterActive = debouncedSearch.trim() !== "" || advancedFilters.length > 0;

  /** A saved view's search, filter tree (fresh local ids) and sorts.
   *  What else a view carries (column widths) is its consumer's. */
  const applyView = useCallback((view: SavedViewRead) => {
    const f = view.filters as Record<string, unknown>;
    setSearch(pickString(f, "search") ?? "");
    setAdvancedFilters(deserializeFilters(f.advanced_filters));
    // Views saved before sorting existed have no `sorts` key.
    setSorts(deserializeSorts(f.sorts));
  }, []);

  const resetFilters = useCallback(() => {
    setSearch("");
    setAdvancedFilters([]);
    setSorts([]);
  }, []);

  /** + Add filter → append a fresh row to the filter tree. */
  const addFilter = useCallback((spec: { field: string; operator: string; value: unknown }) => {
    setAdvancedFilters((prev) => [
      ...prev,
      { id: makeFilterId(), field: spec.field, operator: spec.operator, value: spec.value },
    ]);
  }, []);

  /** Remove a chip — the tab chip resets to "all", an advanced chip drops its row. */
  const removeFilter = useCallback((filter: ActiveFilter) => {
    if (filter.source === "tab") setTab("all");
    else setAdvancedFilters((prev) => prev.filter((f) => f.id !== filter.id));
  }, []);

  /** Edit a chip — the tab chip's editor picks a tab; an advanced chip updates its row. */
  const editFilter = useCallback((filter: ActiveFilter, next: { operator: string; value: unknown }) => {
    if (filter.source === "tab") {
      if (typeof next.value === "string" && TAB_ORDER.includes(next.value as TabKey)) {
        setTab(next.value as TabKey);
      }
      return;
    }
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === filter.id ? { ...f, operator: next.operator, value: next.value } : f)),
    );
  }, []);

  return {
    tab,
    setTab,
    search,
    setSearch,
    debouncedSearch,
    advancedFilters,
    setAdvancedFilters,
    advancedFiltersParam,
    sorts,
    setSorts,
    sortsParam,
    listFilters,
    entity,
    anyFilterActive,
    applyView,
    resetFilters,
    addFilter,
    removeFilter,
    editFilter,
  };
}

export type CompanyFilterState = ReturnType<typeof useCompanyFilterState>;

/**
 * The chips of a company filter state: the tab chip, then one per
 * condition, with what the chip bar and the "+ Add filter" picker need
 * to show and edit them for this project's fields, statuses and members.
 */
export function useCompanyFilterChips(
  state: Pick<CompanyFilterState, "tab" | "advancedFilters">,
  {
    availableColumns,
    pipelineStatuses,
    members,
  }: { availableColumns: AvailableColumn[]; pipelineStatuses: PipelineStatus[]; members: ProjectMember[] },
) {
  const { tab, advancedFilters } = state;
  // Base fields plus whatever the project's available_columns catalog
  // exposes: CFO surfaces funding_stage / founder_origin, M&A surfaces
  // revenue_euros / ebitda_euros / prospect_segment / naf_code.
  const advancedFieldOptions = useMemo<FieldOption[]>(() => buildFieldOptions(availableColumns), [availableColumns]);
  const fieldsWithOverrides = useMemo<FieldOption[]>(
    () => applyLabelOverrides(advancedFieldOptions),
    [advancedFieldOptions],
  );
  const pickerFieldList = useMemo<FieldOption[]>(() => pickerFields(advancedFieldOptions), [advancedFieldOptions]);
  const fieldByKey = useMemo<Record<string, FieldOption>>(
    () => Object.fromEntries(fieldsWithOverrides.map((f) => [f.value, f])),
    [fieldsWithOverrides],
  );
  // Catalogs valueLabelFor uses to show raw stored values (status keys,
  // owner UUIDs) as their labels; anything else is stringified.
  const valueCatalogs = useMemo<Record<string, Record<string, string>>>(
    () => ({
      pipeline_status: Object.fromEntries(pipelineStatuses.map((s) => [s.key, s.label])),
      cfo_action: CFO_ACTION_LABELS,
    }),
    [pipelineStatuses],
  );
  const memberLabelsById = useMemo<Record<string, string>>(
    () => Object.fromEntries(members.map((m) => [m.id, m.full_name])),
    [members],
  );

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const out: ActiveFilter[] = [];
    // 1. Tab chip — only when the tab is not "all". Its editor picks
    //    between the tabs through a synthetic field key.
    const tabSpec = TAB_SPECS[tab];
    if (tabSpec.chip) {
      out.push({
        id: TAB_CHIP_ID,
        source: "tab",
        fieldLabel: tabSpec.chip.fieldLabel,
        operatorLabel: tabSpec.chip.operatorLabel,
        valueLabel: tabSpec.chip.valueLabel,
        fieldKey: "__tab__",
        operator: "eq",
        value: tab,
      });
    }
    // 2. One chip per condition, status and score included.
    for (const cond of advancedFilters) {
      const fieldMeta = fieldByKey[cond.field];
      out.push({
        id: cond.id,
        source: "advanced",
        fieldLabel: fieldMeta?.label ?? cond.field,
        operatorLabel: operatorLabel(OPERATORS_BY_TYPE, fieldMeta?.type ?? "text", cond.operator),
        valueLabel: valueLabelFor(cond, { catalogs: valueCatalogs, members: memberLabelsById }),
        fieldKey: cond.field,
        operator: cond.operator,
        value: cond.value,
      });
    }
    return out;
  }, [tab, advancedFilters, fieldByKey, valueCatalogs, memberLabelsById]);

  /** A chip's editable condition; the tab chip has its own editor. */
  const resolveCondition = useCallback(
    (filter: ActiveFilter): FilterCondition | null =>
      filter.source === "tab" ? null : (advancedFilters.find((f) => f.id === filter.id) ?? null),
    [advancedFilters],
  );

  const resolveField = useCallback(
    (fieldKey: string): FieldOption | null => (fieldKey === "__tab__" ? null : (fieldByKey[fieldKey] ?? null)),
    [fieldByKey],
  );

  return { advancedFieldOptions, pickerFieldList, activeFilters, resolveCondition, resolveField };
}
